import { HueEnum } from '@/modules/onboarding/constants';
import { DeliveryTicketStateEnum, InvoicesStateEnum, DeliveryPackageStateEnum } from '../enums';
import { ICreditCard, ICustomersGetResponse, ICustomersUserAddressesResponse } from '@/shared/endpoints/customers/models/response';
import { IProvider } from './provider';
import { IMedication } from './medication';
import { ITransferPrescriptions } from './transfer-prescriptions';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosRequestConfig } from 'axios';

export interface IFile {
    base64Content: null;
    id: number;
    name: string;
    path: string;
    type: string;
}

export interface ISimpleDropdownOption {
    label: string;
    onClick?: () => void;
    icon?: string;
    link?: string;
}
export interface IHueOption {
    id: HueEnum;
    name: string;
    src: string;
}

export interface IErrorResponse {
    code: string;
    message: string;
    requestMetaData: {
        baseUrl: string;
        method: string;
        url: string;
    };
}

export interface ILoginErrorResponse extends IErrorResponse {
    data: {
        channel: string;
        source: string;
    };
}

export type IResponsePayload<T> = PayloadAction<T | IErrorResponse | undefined>;

export interface IInvoice {
    accountId: number;
    captureAmount: number;
    chargedRefundAmount: number;
    creditCards: Array<ICreditCard>;
    deleted: boolean;
    dueDate: Date | string;
    externalPayment: boolean;
    holdAmount: number;
    holdRefundAmount: number;
    id: number;
    invoiceDate: Date | string;
    invoiceLineItems: Array<IInvoiceLineItem>;
    invoiceNumber: string;
    locationId: number;
    payments: Array<IInvoicePaymentItem>;
    state: InvoicesStateEnum;
    tags: Array<IInvoiceTagItem>;
    total: number;
    totalDueAmount: number;
    unProcessedRefunds: Array<IInvoiceUnProcessedRefundItem>;
    user: ICustomersGetResponse;
    ver: number;
    who: number;
}

export interface IInvoiceLineItem {
    fill: IInvoiceLineItemFill;
    state: string | null;
}

export interface IInvoiceLineItemFill {
    coPay: number;
    fillDate: Date | string;
    prescribable: IMedication;
    prescription: ITransferPrescriptions;
    selfPay: boolean;
}
export interface IInvoicePaymentItem {
    amount: number;
    creditCard: ICreditCard;
    creditCardId: number;
    externalPayment: boolean;
    id: number;
    invoiceId: number;
    parentPaymentId: null;
    refId: string;
    status: 'SUCCESS' | 'FAIL';
    statusMessage: string;
    transactionId: string;
    type: 'CHARGED' | 'UNKNOWN'; // temporary
    ver: number;
    whenTs: Date | string;
    who: number;
}
export interface IInvoiceTagItem {}
export interface IInvoiceUnProcessedRefundItem {}

export interface IDeliveryTicketFill {
    id: number;
    daysSupply: number;
    state: string;
    medication: {
        otc: boolean;
    };
    prescription: {
        refillSetting: 'CYCLE' | 'AUTO' | 'CALL';
        otc: boolean;
        id: number;
        medication: string;
        quantity: number;
        daysSupply: number;
        refills: number;
        state: string;
        medid: number;
        relatedMedId?: number;
    };
    prescribable: {
        name: string;
        otc: boolean;
        medid: string;
    };
    robotMessageId?: number;
}

export interface IDeliveryTicketSupply {
    who: number;
    ver: number;
    id: number;
    name: string;
    effectiveStartDate: string;
    effectiveEndDate: string;
    specification: { color: string };
}

export interface IDeliveryItem {
    fill?: IDeliveryTicketFill;
    supply?: IDeliveryTicketSupply;
    state: DeliveryPackageStateEnum;
}

export enum TransferStateEnum {
    INITIATED = 'INITIATED',
    ON_HOLD = 'ON_HOLD',
    REQUESTED = 'REQUESTED',
    ERROR = 'ERROR',
    DONE = 'DONE'
}

export interface IPendingPrescription {
    id: number;
    handled: boolean;
    prescribable: IMedication;
    prescription?: ITransferPrescriptions;
    provider: IProvider;
    lastUpdatedTs: string;
    transferPrescription: {
        state: TransferStateEnum;
    };
    pharmacy?: {
        name: string;
    };
    prescriptionActionType:
        | 'ERX_REFILL_REQUEST_PENDING'
        | 'FAX_REFILL_REQUEST_PENDING'
        | 'ERX_NEWRX_REQUEST_PENDING'
        | 'FAX_NEWRX_REQUEST_PENDING'
        | 'FAX_TRANSFER_REQUEST_PENDING';
}

export interface IDeliveryPackage {
    who: number;
    ver: number;
    id: number;
    state: DeliveryPackageStateEnum;
    stateTrigger: string;
    stateNote: string | null;
    deliveryCharge: number | null;
    deliveryProof: string | null;
    deliveredAt: string | null;
    undeliverable: boolean | null;
    undeliverableReason: string | null;
    dimensions: string | null;
    dimensionsUnit: string;
    weight: number | null;
    weightUnit: string;
    deleted: boolean;
    deliveryShipment?: {
        estimatedDeliveryDate: string;
        trackingNumber: string;
        carrier: string;
    };
    tags: string[];
    deliveryItems: IDeliveryItem[];
}

export type IDeliveryTicketMode = 'PICKUP' | 'SHIP' | 'DEILVERY';

export interface IDeliveryTicket {
    customerRequestedDeliveryDate: string | null;
    id: number;
    deliveryItems: IDeliveryItem[];
    state: DeliveryTicketStateEnum;
    deliveryPackages: IDeliveryPackage[];
    userAddress: ICustomersUserAddressesResponse;
    user: ICustomersGetResponse;
    mode: IDeliveryTicketMode;
    oneTime?: boolean;
    ver: number;
}

export interface IConvertedShipment extends IDeliveryPackage {
    fullAddress: string;
    address: {
        addressLine1: string;
        addressLine2: string;
        city: string;
        state: string;
        unit: string | null;
        zip: string;
    };
    userName: string;
    ticketId: number;
    ticketState: DeliveryTicketStateEnum;
    customerRequestedDeliveryDate: string;
    productionDate: string;
    ticketMode: IDeliveryTicketMode;
    oneTime: boolean;
}

export interface IPagination {
    per_page: number;
    limit: number;
    current_page: number;
    total: number;
}

export interface IFiltering {
    filter: string;
    direction: 'asc' | 'desc';
}

export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
    timeoutId?: NodeJS.Timeout;
}
